import React, { useState } from 'react';
import { useQuery } from 'react-query';

import { SearchOutlined } from '@ant-design/icons/lib';
import styled from 'styled-components';

import { getMyCommunitiesTable } from 'apis/RecommendationsAPI';
import InputDebounced from 'components/lib/InputDebounced';
import PillSwitcher from 'components/lib/PillSwitcher';
import { PAGINATION_PAGE_SIZE } from 'constants/pagination';
import { canViewPricingData, hasCmiDigestFlag } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';
import { MyCommunitiesQuickFilterType } from 'types/recommendations';

import {
  MY_COMMUNITIES_DEFAULT_RATE_GAP_THRESHOLD_PERCENT,
  MY_COMMUNITIES_DEFAULT_TIME_PERIOD_DAYS,
  MY_COMMUNITIES_QUICK_FILTER_ALL,
  MY_COMMUNITIES_QUICK_FILTER_LABEL,
  MY_COMMUNITIES_QUICK_FILTER_NEEDS_ATTENTION,
  MY_COMMUNITIES_QUICK_FILTER_NEW_COMPETITOR_INCENTIVES,
  MY_COMMUNITIES_QUICK_FILTER_NOTABLE_RATE_GAP,
  MY_COMMUNITIES_QUICK_FILTER_ORDER,
  MY_COMMUNITIES_TIME_PERIOD_OPTIONS,
} from './constants';
import MyCommunitiesTable from './MyCommunitiesTable';
import RateGapThresholdSelect from './RateGapThresholdSelect';
import TimePeriodSelect from './TimePeriodSelect';

const Container = styled.div``;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
`;

const Title = styled.div`
  font-size: 20px;
  font-family: var(--font-bold);
`;

const Search = styled(InputDebounced)`
  width: 320px;
`;

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-top: 20px;
`;

const FilterSelectors = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const FilterDescription = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 10px;
`;

type Props = {
  companyId?: number;
  communityIds: number[];
};

const MyCommunitiesSection = ({ companyId, communityIds }: Props) => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const showPricingData = canViewPricingData(currentUser);
  const showCmiDigest = hasCmiDigestFlag(currentUser);
  const [quickFilter, setQuickFilter] = useState<MyCommunitiesQuickFilterType>(MY_COMMUNITIES_QUICK_FILTER_ALL);
  const [timePeriodDays, setTimePeriodDays] = useState(MY_COMMUNITIES_DEFAULT_TIME_PERIOD_DAYS);
  const [rateGapThresholdPercent, setRateGapThresholdPercent] = useState(
    MY_COMMUNITIES_DEFAULT_RATE_GAP_THRESHOLD_PERCENT
  );
  const [search, setSearch] = useState('');
  const [ordering, setOrdering] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      'my-communities-table',
      companyId,
      communityIds,
      quickFilter,
      timePeriodDays,
      rateGapThresholdPercent,
      search,
      ordering,
      page,
    ],
    queryFn: () =>
      getMyCommunitiesTable(companyId ?? -1, {
        community_ids: communityIds,
        quick_filter: quickFilter,
        time_period_days: timePeriodDays,
        rate_gap_threshold_percent: rateGapThresholdPercent,
        search,
        ordering: ordering ?? undefined,
        page,
        page_size: PAGINATION_PAGE_SIZE.SMALL,
      }),
    enabled: Boolean(companyId),
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const counts = data?.counts;
  const rows = data?.results ?? [];
  const total = data?.count ?? 0;

  const handleQuickFilterChange = (filter: MyCommunitiesQuickFilterType) => {
    setQuickFilter(filter);
    setPage(1);
  };

  const handleTimePeriodChange = (days: number) => {
    setTimePeriodDays(days);
    setPage(1);
  };

  const handleRateGapThresholdChange = (percent: number) => {
    setRateGapThresholdPercent(percent);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleOrderingChange = (value: string | null) => {
    setOrdering(value);
    setPage(1);
  };

  const tabItems = MY_COMMUNITIES_QUICK_FILTER_ORDER.filter(
    (filter) =>
      (showPricingData ||
        (filter !== MY_COMMUNITIES_QUICK_FILTER_NOTABLE_RATE_GAP &&
          filter !== MY_COMMUNITIES_QUICK_FILTER_NEW_COMPETITOR_INCENTIVES)) &&
      (showCmiDigest || filter !== MY_COMMUNITIES_QUICK_FILTER_NEEDS_ATTENTION)
  ).map((filter) => ({
    key: filter,
    label: MY_COMMUNITIES_QUICK_FILTER_LABEL[filter],
    count: counts?.[filter] ?? 0,
  }));

  const timePeriodLabel =
    MY_COMMUNITIES_TIME_PERIOD_OPTIONS.find((option) => option.value === timePeriodDays)?.label ?? '';

  return (
    <Container data-test-id="my-communities-section">
      <Header>
        <Title>My Communities</Title>
        <Search
          prefix={<SearchOutlined />}
          placeholder="Search communities"
          onChange={(e) => handleSearchChange(e?.target?.value ?? '')}
        />
      </Header>
      <FiltersRow>
        <PillSwitcher
          items={tabItems}
          activeKey={quickFilter}
          onChange={handleQuickFilterChange}
          getItemTestId={(key) => `my-communities-quick-filter-${key}`}
        />
        <FilterSelectors>
          {showPricingData && quickFilter === MY_COMMUNITIES_QUICK_FILTER_NOTABLE_RATE_GAP && (
            <RateGapThresholdSelect value={rateGapThresholdPercent} onChange={handleRateGapThresholdChange} />
          )}
          <TimePeriodSelect value={timePeriodDays} onChange={handleTimePeriodChange} />
        </FilterSelectors>
      </FiltersRow>
      {quickFilter === MY_COMMUNITIES_QUICK_FILTER_NOTABLE_RATE_GAP && (
        <FilterDescription data-test-id="my-communities-notable-rate-gap-description">
          {`Min rate is at least ${rateGapThresholdPercent}% above or below the average competitor min rate for the same care type and room type.`}
        </FilterDescription>
      )}
      <MyCommunitiesTable
        rows={rows}
        loading={isLoading || isFetching}
        page={page}
        pageSize={PAGINATION_PAGE_SIZE.SMALL}
        total={total}
        showPricingData={showPricingData}
        ordering={ordering}
        timePeriodDays={timePeriodDays}
        timePeriodLabel={timePeriodLabel}
        onPageChange={setPage}
        onOrderingChange={handleOrderingChange}
      />
    </Container>
  );
};

export default MyCommunitiesSection;
